/**
 * Drawing and Annotation Types
 *
 * Defines all drawing-related TypeScript interfaces and types for the
 * GeoVera WebGIS Platform Drawing and Annotation Tools.
 *
 * Validates: Requirements 39, 40
 */
import type { Geometry } from 'geojson';

// ---------------------------------------------------------------------------
// Drawing tool types
// ---------------------------------------------------------------------------

/** Available drawing tool modes */
export type DrawingToolType = 'point' | 'line' | 'polygon' | 'circle' | 'rectangle' | 'text' | null;

/** Drawing shape types (excludes text) */
export type DrawingShapeType = 'point' | 'line' | 'polygon' | 'circle' | 'rectangle';

// ---------------------------------------------------------------------------
// Drawing style interfaces
// ---------------------------------------------------------------------------

/**
 * Style configuration for drawn shapes.
 * Requirement 39.4: color, fill, stroke width, opacity.
 */
export interface DrawingStyle {
  /** Fill color as hex string (e.g. '#3388ff') */
  fillColor: string;
  /** Stroke/outline color as hex string */
  strokeColor: string;
  /** Stroke width in pixels */
  strokeWidth: number;
  /** Opacity in range [0, 1] */
  opacity: number;
}

/**
 * Style configuration for text annotations.
 * Requirement 40.4: font, size, color, background.
 */
export interface TextStyle {
  /** Font family name */
  font: string;
  /** Font size in pixels */
  fontSize: number;
  /** Text color as hex string */
  fontColor: string;
  /** Background color as hex string (empty string for no background) */
  backgroundColor: string;
}

// ---------------------------------------------------------------------------
// Drawing data interfaces
// ---------------------------------------------------------------------------

/**
 * A single drawing element on the map.
 * Can be a shape or text annotation.
 */
export interface Drawing {
  /** Unique identifier */
  id: string;
  /** Type of drawing */
  type: DrawingToolType;
  /** GeoJSON geometry of the drawing */
  geometry: Geometry;
  /** Style applied to the drawing (for shapes) */
  style: DrawingStyle;
  /** Text content (for text annotations) */
  text?: string;
  /** Text style (for text annotations) */
  textStyle?: TextStyle;
  /** Timestamp of creation */
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------

/** Default drawing style for shapes */
export const DEFAULT_DRAWING_STYLE: DrawingStyle = {
  fillColor: '#3388ff',
  strokeColor: '#ffffff',
  strokeWidth: 2,
  opacity: 0.7,
};

/** Default text annotation style */
export const DEFAULT_TEXT_STYLE: TextStyle = {
  font: 'Arial',
  fontSize: 14,
  fontColor: '#000000',
  backgroundColor: '#ffffff',
};
